import { useMemo, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Database, LayoutGrid, List, ListChecks, Settings } from 'lucide-react';
import { useRegisterCommands, type PaletteCommand } from '@/components/ui/workspace';
import { IntakeRunWatcher } from '@/components/requirement-library/intake/intake-run-watcher';
import { RecordSearchCommands } from './record-search';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';

/**
 * App shell (T-0096 / E10).
 *
 * The top-level chrome rendered by `routes/__root.tsx`. Replaces the
 * legacy single-row `<AppHeader />` from the scaffold with the
 * prototype's two-axis layout:
 *
 *   ┌────────────┬──────────────────────────────┐
 *   │            │  topbar (breadcrumb · user)  │
 *   │  sidebar   ├──────────────────────────────┤
 *   │ (240px)    │                              │
 *   │            │  <Outlet />                  │
 *   │            │                              │
 *   └────────────┴──────────────────────────────┘
 *
 * Mirrors `.app { grid-template-columns: 240px 1fr }` from
 * `design_handoff_caliber_v2/prototype/tokens.css`.
 *
 * Per the ticket scope this is where the SnapshotBanner auto-mounts when
 * the user is viewing a frozen snapshot. T-0095 (snapshot banner) hasn't
 * merged to main yet, so the slot is left as a comment marker that the
 * downstream ticket can wire without further structural change.
 *
 * T-0157 mounts the {@link IntakeRunWatcher} here, once for the signed-in
 * session: it tells a curator at once, on any page, when one of their AI
 * document imports fails or is ready for review.
 *
 * T-0227 mounts {@link RecordSearchCommands} here, once: the palette then
 * finds clients and engagements by name on every page, and documents by
 * title inside an engagement.
 */
export function AppShell({ children }: { children: ReactNode }) {
  // App-wide "Go to" commands (T-0141), so the palette is useful on every
  // screen. Screens add their own with `useRegisterCommands`.
  const navigate = useNavigate();
  const commands = useMemo<PaletteCommand[]>(
    () => [
      {
        id: 'app:portfolio',
        group: 'Go to',
        label: 'Portfolio',
        icon: <LayoutGrid className="h-3.5 w-3.5" />,
        run: () => void navigate({ to: '/' }),
      },
      {
        id: 'app:clients',
        group: 'Go to',
        label: 'Clients',
        icon: <Building2 className="h-3.5 w-3.5" />,
        run: () => void navigate({ to: '/clients' }),
      },
      {
        id: 'app:settings',
        group: 'Go to',
        label: 'Firm settings',
        icon: <Settings className="h-3.5 w-3.5" />,
        run: () => void navigate({ to: '/settings' }),
      },
      {
        id: 'app:requirement-library',
        group: 'Go to',
        label: 'Requirement library',
        keywords: ['firm settings', 'requirements', 'modules'],
        icon: <ListChecks className="h-3.5 w-3.5" />,
        run: () => void navigate({ to: '/settings/requirement-library' }),
      },
      {
        id: 'app:discovery-frameworks',
        group: 'Go to',
        label: 'Discovery frameworks',
        keywords: ['firm settings', 'discovery', 'areas', 'interview types', 'questionnaires'],
        icon: <List className="h-3.5 w-3.5" />,
        run: () => void navigate({ to: '/settings/discovery-frameworks' }),
      },
      {
        id: 'app:vendor-knowledge',
        group: 'Go to',
        label: 'Vendor knowledge base',
        keywords: ['firm settings', 'vendors', 'products', 'partners'],
        icon: <Database className="h-3.5 w-3.5" />,
        run: () => void navigate({ to: '/settings/vendor-knowledge' }),
      },
    ],
    [navigate],
  );
  useRegisterCommands(commands);

  return (
    <div className="grid min-h-screen grid-cols-[240px_1fr] bg-paper-50">
      {/*
        Snapshot banner slot: when viewing a frozen snapshot route, the
        SnapshotBanner primitive from T-0095 (apps/web/src/components/ui/
        snapshot-banner.tsx) auto-mounts here above the sidebar/main
        layout. Wire it in once T-0095 lands.
      */}
      <IntakeRunWatcher />
      <RecordSearchCommands />
      <Sidebar />
      <div className="flex min-w-0 flex-col">
        <Topbar />
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}
